import axios from 'axios';
import secureStorage from '../../../utils/secureStorage';

const WORKSPACE_STORAGE_KEY = 'busin.us-workspaces';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export class WorkspaceService {
  constructor() {
    this.session = secureStorage.getItem('auth_session') || localStorage.getItem('auth_session');
    this.workspaces = this.loadFromCache();
  }

  setSession(session) {
    this.session = session;
  }

  getHeaders() {
    return {
      Authorization: `Bearer ${this.session}`
    };
  }

  loadFromCache() {
    try {
      const saved = localStorage.getItem(WORKSPACE_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error("Cache load failed", e);
      return [];
    }
  }

  saveToCache(workspaces) {
    try {
      localStorage.setItem(WORKSPACE_STORAGE_KEY, JSON.stringify(workspaces));
      this.workspaces = workspaces;
    } catch (e) {
      console.error("Cache save failed", e);
    }
  }

  async syncWithCloud() {
    if (!this.session) return this.workspaces;

    try {
      const response = await axios.get(`${BACKEND_URL}/api/workflows`, {
        headers: this.getHeaders()
      });
      if (response.data.success) {
        this.saveToCache(response.data.workflows);
        return response.data.workflows;
      }
    } catch (e) {
      console.error("Cloud sync failed, using cache", e);
    }
    return this.workspaces;
  }

  getAllWorkspaces() {
    // For non-authenticated users, return cached workspaces immediately
    if (!this.session) {
      return this.workspaces || [];
    }
    
    // For authenticated users, try to sync with cloud but return a promise
    return this.syncWithCloud();
  }
  
  // Synchronous version for immediate access to cached data
  getAllWorkspacesSync() {
    return this.workspaces || [];
  }

  getWorkspaceById(id) {
    return this.workspaces.find(w => w.id === id);
  }

  async createWorkspace(name, workflowData = null) {
    const payload = {
      name: name || `Workspace ${this.workspaces.length + 1}`,
      description: '',
      canvas_data: workflowData || { nodes: [], edges: [], groups: [] }
    };

    if (this.session) {
      try {
        const response = await axios.post(`${BACKEND_URL}/api/workflows`, payload, {
          headers: this.getHeaders()
        });
        if (response.data.success) {
          await this.syncWithCloud();
          return response.data.workflow;
        }
      } catch (e) {
        console.error("Cloud create failed", e);
      }
    }

    // Fallback to local
    const newLocal = {
      ...payload,
      id: `local_${Date.now()}`,
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString()
    };
    this.workspaces.push(newLocal);
    this.saveToCache(this.workspaces);
    return newLocal;
  }

  async updateWorkspace(id, updates) {
    const workspace = this.getWorkspaceById(id);
    if (!workspace) return null;

    const merged = { ...workspace, ...updates };

    if (this.session && id && typeof id === 'string' && !id.startsWith('local_')) {
      try {
        const response = await axios.post(`${BACKEND_URL}/api/workflows`, {
          id,
          name: merged.name,
          description: merged.description,
          canvas_data: merged.canvas_data || updates // Handling different update structures
        }, {
          headers: this.getHeaders()
        });
        if (response.data.success) {
          await this.syncWithCloud();
          return response.data.workflow;
        }
      } catch (e) {
        console.error("Cloud update failed", e);
      }
    }

    // Update Cache
    const index = this.workspaces.findIndex(w => w.id === id);
    if (index !== -1) {
      this.workspaces[index] = { ...this.workspaces[index], ...updates, lastModified: new Date().toISOString() };
      this.saveToCache(this.workspaces);
      return this.workspaces[index];
    }
    return null;
  }

  async deleteWorkspace(id) {
    if (this.session && id && typeof id === 'string' && !id.startsWith('local_')) {
      try {
        const response = await axios.delete(`${BACKEND_URL}/api/workflows/${id}`, {
          headers: this.getHeaders()
        });
        if (response.data.success) {
          await this.syncWithCloud();
          return true;
        }
      } catch (e) {
        console.error("Cloud delete failed", e);
      }
    }

    // Delete from cache
    this.workspaces = this.workspaces.filter(w => w.id !== id);
    this.saveToCache(this.workspaces);
    return true;
  }

  async importToNewWorkspace(name, workflowData) {
    const payload = {
      name: name || `Imported Workspace ${this.workspaces.length + 1}`,
      description: 'Imported from workflow file',
      canvas_data: workflowData || { nodes: [], edges: [], groups: [] }
    };

    return await this.createWorkspace(payload.name, payload.canvas_data);
  }

  async inviteCollaborator(workflowId, email, role) {
    if (!this.session) return null;
    try {
      const response = await axios.post(`${BACKEND_URL}/api/workflows/${workflowId}/invite`, {
        email, role
      }, { headers: this.getHeaders() });
      return response.data;
    } catch (e) {
      console.error("Invite failed", e);
      return { error: 'Failed to send invitation' };
    }
  }
}

const workspaceService = new WorkspaceService();
export default workspaceService;